<template>
  <b-modal
    id="modal-exportar-catalogo"
    v-model="show"
    title="Exportar catálogo"
    size="md"
    centered
    no-close-on-backdrop
    :no-close-on-esc="exportando"
    :hide-header-close="exportando"
    hide-footer
    @show="onShow"
    @hidden="onHidden"
  >
    <div v-if="exportando" class="text-center py-2">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
      <h5 class="mt-2 mb-1">Gerando o arquivo...</h5>
      <p class="text-muted mb-0">Montando a planilha do catálogo. Não feche esta janela.</p>
    </div>

    <div v-else>
      <b-alert variant="info" show class="mb-2">
        <div class="alert-body">
          <p class="mb-0">
            Selecione um ou mais <strong>tipos de pedido</strong>. O arquivo Excel terá
            uma <strong>aba separada por tipo</strong>, com os produtos disponíveis em cada um.
          </p>
        </div>
      </b-alert>

      <div v-if="carregandoTipos" class="text-center py-2">
        <b-spinner small variant="primary" class="mr-50" />
        Carregando tipos de pedido...
      </div>

      <b-alert v-else-if="!tipos.length" variant="warning" show class="mb-0">
        <div class="alert-body">
          <feather-icon icon="AlertTriangleIcon" class="mr-50" />
          Nenhum tipo de pedido disponível para exportar.
        </div>
      </b-alert>

      <template v-else>
        <b-form-checkbox
          class="mb-1 font-weight-bold"
          :checked="todosSelecionados"
          :indeterminate="algunsSelecionados"
          @change="onToggleTodos"
        >
          Selecionar todos
        </b-form-checkbox>

        <hr class="my-1">

        <b-form-checkbox-group v-model="selecionados" stacked>
          <b-form-checkbox
            v-for="tp in tipos"
            :key="tp.tipopedidoid"
            :value="tp.tipopedidoid"
            class="mb-50"
          >
            {{ tp.tipopedidoid }} - {{ tp.tipopedidodescricao }}
          </b-form-checkbox>
        </b-form-checkbox-group>

        <div class="d-flex justify-content-end mt-2">
          <b-button variant="outline-secondary" class="mr-1" @click="show = false">
            Cancelar
          </b-button>
          <b-button
            variant="primary"
            :disabled="!selecionados.length"
            @click="exportar"
          >
            <feather-icon icon="DownloadIcon" class="mr-50" />
            Exportar {{ selecionados.length ? `(${selecionados.length})` : '' }}
          </b-button>
        </div>
      </template>
    </div>
  </b-modal>
</template>

<script>
import EsMarcaClient from '@/api/SistemaDKP/sls-marca.client';
import SLSTipoPedidoService from '@/services/SistemaDKP/SLSTipoPedido.service';
import Notifier from '@/libs/Notifier';

export default {
  name: 'ModalExportarCatalogo',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      marcaClient: new EsMarcaClient(),
      tipoPedidoService: new SLSTipoPedidoService(),
      notify: new Notifier(),
      tipos: [],
      selecionados: [],
      carregandoTipos: false,
      exportando: false,
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    todosSelecionados() {
      return this.tipos.length > 0 && this.selecionados.length === this.tipos.length;
    },
    algunsSelecionados() {
      return this.selecionados.length > 0 && this.selecionados.length < this.tipos.length;
    },
  },
  methods: {
    async onShow() {
      this.selecionados = [];
      await this.carregarTipos();
    },
    onHidden() {
      this.selecionados = [];
    },
    async carregarTipos() {
      this.carregandoTipos = true;
      try {
        const response = await this.tipoPedidoService.index({ perPage: 100 });
        const items = Array.isArray(response) ? response : (response?.data || []);
        this.tipos = items
          .filter((t) => t && t.tipopedidoid)
          .map((t) => ({
            tipopedidoid: t.tipopedidoid,
            tipopedidodescricao: t.tipopedidodescricao || t.tipopedidoid,
          }));
      } catch (error) {
        this.notify.error('Erro ao carregar tipos de pedido');
        this.tipos = [];
      } finally {
        this.carregandoTipos = false;
      }
    },
    onToggleTodos(marcar) {
      this.selecionados = marcar ? this.tipos.map((t) => t.tipopedidoid) : [];
    },
    async exportar() {
      if (!this.selecionados.length) return;
      this.exportando = true;
      try {
        const payload = this.selecionados.map((id) => {
          const tp = this.tipos.find((t) => t.tipopedidoid === id);
          return {
            tipopedidoid: id,
            tipopedidodescricao: tp ? tp.tipopedidodescricao : id,
          };
        });
        const response = await this.marcaClient.exportarCatalogo(payload);
        this.baixarArquivo(response);
        this.show = false;
      } catch (error) {
        this.notify.error('Erro ao exportar o catálogo');
      } finally {
        this.exportando = false;
      }
    },
    baixarArquivo(response) {
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const dataStr = new Date().toISOString().slice(0, 10);
      let filename = `catalogo_${dataStr}.xlsx`;
      const disposition = response.headers && response.headers['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>
